import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import { useFolderQuery } from 'components/hooks/folderQueries';

import DestroyModal from 'components/application/DestroyModal';
import RadioField from 'components/application/RadioField';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

export default function DestroyFolderModal({
  folderId,
  isDestroyDisabled = false,
  onClose,
  onDestroy,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.DestroyFolderModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [projectsAction, setProjectsAction] = useState('archive');

  const { data: domainFolder } = useFolderQuery({ id: folderId });

  if (!domainFolder) return null;

  const {
    attributes: { name: folderName },
    meta: { projectCount },
  } = domainFolder.data;

  const ramsName = pluralizeProjectsName({
    count: projectCount,
    currentActor,
  });

  const { pluralRAMSName } = currentActor.division.attributes;

  return (
    <DestroyModal
      additionalContent={
        projectCount > 0 ?
          <div className='tw-mt-6 tw-flex tw-flex-col tw-gap-4 tw-text-grey-700'>
            <p className='tw-mb-0 tw-text-m tw-font-semibold'>
              {i18nT('additionalContent.withProjects.description', {
                count: projectCount,
                interpolation: { escapeValue: false },
                ramsName,
              })}
            </p>
            <RadioField
              checked={projectsAction === 'archive'}
              className='tw-m-0'
              key='archive'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-text-grey-700'>
                  {i18nTCommon('shared.archiveName', {
                    interpolation: { escapeValue: false },
                    name: ramsName,
                  })}
                </p>
              }
              name='projects_action'
              onChange={() => setProjectsAction('archive')}
              value='archive'
            />
            <RadioField
              checked={projectsAction === 'division'}
              className='tw-m-0'
              key='division'
              label={
                <>
                  <p className='tw-mb-1 tw-text-m tw-font-normal tw-text-grey-700'>
                    {i18nT('additionalContent.withProjects.division', {
                      interpolation: { escapeValue: false },
                      ramsName,
                    })}
                  </p>
                  <p className='tw-mb-0 tw-text-s tw-font-normal tw-tracking-wide tw-text-grey-500'>
                    {i18nT(
                      'additionalContent.withProjects.divisionDisclaimer',
                      {
                        interpolation: { escapeValue: false },
                        pluralRAMSName: voca.capitalize(pluralRAMSName),
                      },
                    )}
                  </p>
                </>
              }
              name='projects_action'
              onChange={() => setProjectsAction('division')}
              value='division'
            />
          </div>
        : <p className='tw-mb-0 tw-mt-6 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {i18nT('additionalContent.withoutProjects', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            })}
          </p>
      }
      confirmationText={i18nTCommon('projects.deleteFolder')}
      displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
        interpolation: { escapeValue: false },
        name: folderName,
      })}
      isDestroyDisabled={isDestroyDisabled}
      isOpen={true}
      onClose={onClose}
      onDestroy={() => {
        onDestroy({
          id: folderId,
          name: folderName,
          params: projectCount > 0 ? { projects_action: projectsAction } : {},
          projectCount,
        });
      }}
    />
  );
}

DestroyFolderModal.propTypes = {
  folderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDestroy: PropTypes.func.isRequired,
};
